import React from "react";
import { StyleSheet } from "react-native";
import { ColumnLayout } from "~/components/ColumnLayout";
import type { IconProps } from "~/components/Icon";
import { Icon } from "~/components/Icon";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { View } from "~/components/View";
import { styleConstants } from "~/style/styleConstants";
import { hexToRgba } from "~/utils/hexToRgba";
import { useResponsive } from "../live-chat/useResponsive";

export function SectionFeatures() {
  const r = useResponsive();

  const separator = r(
    <Spacer small />,
    <Spacer large />,
    <Spacer extraLarge />,
  );

  return (
    <View centerContent paddingLarge>
      <ColumnLayout
        numColumns={r(2, 2, 4)}
        columnSeparator={separator}
        rowSeparator={separator}
      >
        <Card
          iconName="flash"
          title={intl.formatMessage({
            defaultMessage: "Fast",
            id: "b21fZv",
            description: "Features section - Card title",
          })}
          subtitle={intl.formatMessage({
            defaultMessage: "P2P technology ensures fast connections",
            id: "O2wRHu",
            description: "Features section - Card subtitle",
          })}
        />
        <Card
          iconName="lock"
          title={intl.formatMessage({
            defaultMessage: "Private",
            id: "soLoBl",
            description: "Features section - Card title",
          })}
          subtitle={intl.formatMessage({
            defaultMessage:
              "Our system makes sure your video is only visible by you and your partner",
            id: "aB322K",
            description: "Features section - Card subtitle",
          })}
        />

        <Card
          iconName="security"
          title={intl.formatMessage({
            defaultMessage: "Secure",
            id: "5Z0t+N",
            description: "Features section - Card title",
          })}
          subtitle={intl.formatMessage({
            defaultMessage: "Modern technologies ensure a secure connection",
            id: "C1XyrH",
            description: "Features section - Card subtitle",
          })}
        />

        <Card
          iconName="shield-check"
          title={intl.formatMessage({
            id: "BtMU62",
            defaultMessage: "Moderated",
            description: "Features section - Card title",
          })}
          subtitle={intl.formatMessage({
            defaultMessage: "Our AI systems analyze and block bad actors",
            id: "F0a696",
            description: "Features section - Card subtitle",
          })}
        />
      </ColumnLayout>

      <Spacer extraLarge />
      <Spacer extraLarge />
      <Spacer large />

      <View style={{ maxWidth: 1320, marginHorizontal: "auto" }}>
        <Text
          sizeExtraLarge
          weightExtraBold
          alignCenter
          style={{ fontSize: 32 }}
        >
          {intl.formatMessage({
            defaultMessage: "What is BokBok Live?",
            id: "bXTh3w",
            description: "Features section - Title",
          })}
        </Text>
        <Spacer extraLarge />
        <Text>
          {intl.formatMessage(
            {
              defaultMessage:
                "BokBok Live is a <bold>Croatian random video chat platform</bold> that randomly connects you with people from Croatia and the region. It operates on the Chatroulette principle: the system automatically pairs you with a new interlocutor via camera.\n\nUnlike standard apps, there is no lengthy registration process. This is a fast and simple addition to the <bold>BokBok Dating</bold> app. You simply enter a nickname and your age to immediately start a <bold>video chat with no sign-up</bold>. Try the fastest way to meet online and talk live.",
              id: "vyCCku",
              description: "Features section - Description",
            },
            {
              bold: (chunks: React.ReactNode) => (
                <Text weightSemiBold>{chunks}</Text>
              ),
            },
          )}
        </Text>
        <Spacer />
        <Text>
          {intl.formatMessage({
            defaultMessage:
              "Our app is designed to be fast and simple, ensuring you can start meaningful conversations without any hassle. With high-quality video and audio, you’ll feel like you’re in the same room, no matter where you are. Making authentic, real-time connections in the digital realm has never been easier!",
            id: "S0zBVT",
            description: "Features section - Description",
          })}
        </Text>

        <Spacer extraLarge />
        <Spacer extraLarge />
      </View>
    </View>
  );
}

const Card = ({
  iconName,
  title,
  subtitle,
}: {
  iconName: IconProps["name"];
  title: string;
  subtitle: string;
}) => {
  const r = useResponsive();

  return (
    <View
      style={[
        S.card,
        {
          maxWidth: r(250, 250, 300),
          minWidth: r(175, 200, undefined),
          padding: r(
            styleConstants.spacingSmall,
            styleConstants.spacingLarge,
            styleConstants.spacingExtraLarge,
          ),
        },
      ]}
    >
      <View alignItemsCenter justifyContentFlexEnd flex>
        <Icon name={iconName} color={styleConstants.colorTextTheme} size={38} />
        <Spacer extraSmall />
        <Text colorTheme weightExtraBold sizeLarge>
          {title}
        </Text>
      </View>
      <Spacer />
      <View flex>
        <Text
          alignCenter
          sizeSmall={r(true, false, false)}
          sizeMedium={r(false, true, true)}
        >
          {subtitle}
        </Text>
      </View>
    </View>
  );
};

const S = StyleSheet.create({
  card: {
    borderRadius: styleConstants.borderRadiusMedium,
    borderColor: hexToRgba(styleConstants.colorTextDark, 0.2),
    borderWidth: 1,
    flex: 1,

    aspectRatio: 1,
    justifyContent: "center",
  },
  cardsContainer: {
    gap: styleConstants.spacingLarge,
  },
});
